import { FC } from "react";

import {
  FormCategory,
  FormQueryParams,
  FormType,
  getDonationOrTicketingEmbedLink,
  getEmbedLeaderboardLink,
  getEmbedThermometerLink,
  Locales,
} from "@simplyk/common";

import { SimpleEmbedInstructionsTab, SimpleEmbedInstructionsTabProps } from "./SimpleEmbedInstructionsTab";

import {
  ButtonPopupInstructionsTabProps,
  ButtonPopupInstructionsTab,
} from "@/components/ShareFormModal/ButtonPopupInstructionsTab";
import {
  LinkedButtonInstructionsTabProps,
  LinkedButtonInstructionsTab,
} from "@/components/ShareFormModal/LinkedButtonInstructionsTab";
import { EMBEDDED_BUTTON_LINK_ATTR } from "@/constants/EmbedForm";
import { FRONTEND_URL } from "@/routes";

type FormEmbedInstructionsTabProps = Omit<SimpleEmbedInstructionsTabProps, "embedCode" | "guideLinks" | "embedType"> & {
  formId: string;
  formPath: string;
  formType: FormType;
  isPrimaryCampaign?: boolean;
  isPrimaryTeam?: boolean;
};

export const FormEmbedInstructionsTab: FC<FormEmbedInstructionsTabProps> = ({
  formId,
  formPath,
  formType,
  formCategory,
  isPrimaryCampaign,
  isPrimaryTeam,
  ...rest
}) => {
  const guideLinks = {
    [Locales.EN]: "https://support.zeffy.com/how-do-i-share-my-form#embed",
    [Locales.FR]: "https://support.zeffy.com/fr-ca/comment-puis-je-partager-mon-formulaire#integrer",
    [Locales.ES]: "https://support.zeffy.com/how-do-i-share-my-form#embed",
  };

  const embedPath = getDonationOrTicketingEmbedLink({
    form: {
      id: formId,
      path: formPath,
      formType,
      formCategory: FormCategory[formCategory],
      isPrimaryCampaign,
      isPrimaryTeam,
    },
    isoLocale: null,
  });

  const embedLink = `${FRONTEND_URL}${embedPath}`;

  const embedCode =
    formType === FormType.DonationForm
      ? getEmbedHtml({ src: embedLink, iframHeight: "100%", divHeight: "450px", allowPaymentRequest: true })
      : getEmbedHtml({
          src: embedLink,
          iframHeight: "100%",
          divHeight: "450px",
          divPaddingTop: "450px",
          allowPaymentRequest: true,
        });

  return (
    <SimpleEmbedInstructionsTab
      {...rest}
      embedType="Form"
      embedCode={embedCode}
      guideLinks={guideLinks}
      formCategory={formCategory}
    />
  );
};

type ThermometerEmbedInstructionsTabProps = Omit<
  SimpleEmbedInstructionsTabProps,
  "embedCode" | "guideLinks" | "embedType"
> & {
  formId: string;
  formPath: string;
};

export const ThermometerEmbedInstructionsTab: FC<ThermometerEmbedInstructionsTabProps> = ({
  formId,
  formPath,
  ...rest
}) => {
  const guideLinks = {
    [Locales.EN]: "https://support.zeffy.com/how-do-i-share-my-form#thermometer",
    [Locales.FR]: "https://support.zeffy.com/fr-ca/comment-puis-je-partager-mon-formulaire#thermometre",
    [Locales.ES]: "https://support.zeffy.com/how-do-i-share-my-form#thermometer",
  };

  const embedCode = getEmbedHtml({
    src: `${FRONTEND_URL}${getEmbedThermometerLink(formPath || formId, null)}`,
    iframHeight: "120px",
    divPaddingTop: "120px",
  });

  return (
    <SimpleEmbedInstructionsTab
      {...rest}
      embedType="Thermometer"
      embedCode={embedCode}
      guideLinks={guideLinks}
      formCategory={rest.formCategory}
    />
  );
};

type LeaderboardEmbedInstructionsTabProps = Omit<
  SimpleEmbedInstructionsTabProps,
  "embedCode" | "guideLinks" | "embedType"
> & {
  formId: string;
  formPath: string;
};

export const LeaderboardEmbedInstructionsTab: FC<LeaderboardEmbedInstructionsTabProps> = ({
  formId,
  formPath,
  ...rest
}) => {
  const guideLinks = {
    [Locales.EN]: "https://support.zeffy.com/how-do-i-share-my-form#leaderboard",
    [Locales.FR]: "https://support.zeffy.com/fr-ca/comment-puis-je-partager-mon-formulaire#leaderboard",
    [Locales.ES]: "https://support.zeffy.com/how-do-i-share-my-form#leaderboard",
  };

  const embedCode = getEmbedHtml({
    src: `${FRONTEND_URL}${getEmbedLeaderboardLink(formPath || formId, null)}`,
    divPaddingTop: "240px",
    iframHeight: "100%",
  });

  return (
    <SimpleEmbedInstructionsTab
      {...rest}
      embedType="Leaderboard"
      embedCode={embedCode}
      guideLinks={guideLinks}
      formCategory={rest.formCategory}
    />
  );
};

type ButtonPopupEmbedInstructionsTabProps = Omit<
  ButtonPopupInstructionsTabProps,
  "embedCode" | "guideLinks" | "embedType"
> & {
  formId: string;
  formPath: string;
  formType: FormType;
  formCategory: FormCategory;
  isPrimaryCampaign: boolean;
  isPrimaryTeam: boolean;
};

export const ButtonPopupEmbedInstructionsTab: FC<ButtonPopupEmbedInstructionsTabProps> = ({
  formId,
  formPath,
  formType,
  formCategory,
  isPrimaryCampaign,
  isPrimaryTeam,
  ...rest
}) => {
  const guideLinks = {
    [Locales.EN]: "https://support.zeffy.com/how-do-i-share-my-form#pop-up",
    [Locales.FR]: "https://support.zeffy.com/fr-ca/comment-puis-je-partager-mon-formulaire#pop-up",
    [Locales.ES]: "https://support.zeffy.com/how-do-i-share-my-form#pop-up",
  };

  const embedPath = getDonationOrTicketingEmbedLink({
    form: {
      id: formId,
      path: formPath,
      formType,
      formCategory: FormCategory[formCategory],
      isPrimaryCampaign,
      isPrimaryTeam,
    },
    isoLocale: null,
  });

  const embedLink = `${FRONTEND_URL}${embedPath}`;

  const embedCode = `${EMBEDDED_BUTTON_LINK_ATTR}="${embedLink}?${FormQueryParams.Modal}=true"`;

  return (
    <ButtonPopupInstructionsTab {...rest} guideLinks={guideLinks} embedCode={embedCode} formCategory={formCategory} />
  );
};

type LinkedButtonEmbedInstructionsTabProps = Omit<LinkedButtonInstructionsTabProps, "formLink" | "guideLinks"> & {
  formLink: string;
};

export const LinkedButtonEmbedInstructionsTab: FC<LinkedButtonEmbedInstructionsTabProps> = ({ formLink, ...rest }) => {
  const guideLinks = {
    [Locales.EN]: "https://support.zeffy.com/add-a-donate-button-to-a-website",
    [Locales.FR]: "https://support.zeffy.com/fr-ca/ajouter-un-bouton-%C3%A0-un-site-web-pour-partager-votre-formulaire",
    [Locales.ES]: "https://support.zeffy.com/add-a-donate-button-to-a-website",
  };

  return <LinkedButtonInstructionsTab {...rest} guideLinks={guideLinks} formLink={formLink} />;
};

function getEmbedHtml({
  src,
  iframHeight,
  divHeight,
  divPaddingTop,
  allowPaymentRequest,
}: {
  src: string;
  iframHeight: string;
  divHeight?: string;
  divPaddingTop?: string;
  allowPaymentRequest?: boolean;
}) {
  // eslint-disable-next-line sonarjs/no-nested-template-literals
  return `<div style="position:relative;overflow:hidden;${divHeight ? `height:${divHeight};` : ""}width:100%;${
    divPaddingTop ? `padding-top:${divPaddingTop};` : ""
  }"><iframe title='Donation form powered by Zeffy' style='position: absolute; border: 0; top:0;left:0;bottom:0;right:0;width:100%;height:${iframHeight}' src='${src}' ${
    allowPaymentRequest ? "allowpaymentrequest" : ""
  } allowTransparency="true"></iframe></div>`;
}
